import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import styles from './WorkplaceTypeContainer.css';
import { DropdownFilter, DropdownSelect, useDropdown } from 'shared/components/DropdownFilter';
import { useFilterChangeCallback, useShowSearchMetaTooltip } from 'shared/hooks';
import { selectCoworkingOfferType } from 'shared/selectors/jsonQuery/selectCoworkingOfferType';
import { selectIsInlineFiltersRedesigned } from 'shared/selectors/redesigned/selectIsInlineFiltersRedesigned';
import { selectWorkplaceTypeFilter } from 'shared/selectors/filters/workplaceType';
import { TSelectValue } from 'shared/types/filters';

import { ECoworkingOfferType } from '../../../packages/api-models/common/json_query';

import { FILTER_KEY } from './constants';

const WORKPLACE_VALUES: ECoworkingOfferType[] = [ECoworkingOfferType.FixedWorkSpace, ECoworkingOfferType.FreeWorkSpace];

export const WorkplaceTypeContainer: FC = () => {
  const { label, value, options } = useSelector(selectWorkplaceTypeFilter);
  const coworkingOfferType = useSelector(selectCoworkingOfferType);
  const isFiltersRedesignEnabled = useSelector(selectIsInlineFiltersRedesigned);

  const dropdownState = useDropdown(FILTER_KEY);
  const showTooltip = useShowSearchMetaTooltip(FILTER_KEY);

  const onChange = useFilterChangeCallback(FILTER_KEY);

  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const displayValue = useMemo(() => {
    if (!hasUserInteracted && value.length === WORKPLACE_VALUES.length) {
      return [];
    }

    return value;
  }, [hasUserInteracted, value]);

  const handleChange = useCallback(
    (next: TSelectValue) => {
      setHasUserInteracted(true);
      const nextWorkplaceValues = (Array.isArray(next) ? next : []) as ECoworkingOfferType[];
      const otherValues = (coworkingOfferType ?? []).filter(v => !WORKPLACE_VALUES.includes(v));
      const effectiveValues =
        nextWorkplaceValues.length === 0 && otherValues.length === 0 ? WORKPLACE_VALUES : nextWorkplaceValues;
      const merged = [...otherValues, ...effectiveValues];

      onChange({ action: 'setCoworkingOfferType', arguments: [merged.length ? merged : null] });
    },
    [onChange, coworkingOfferType],
  );

  return (
    <DropdownFilter
      {...dropdownState}
      buttonLabel={label}
      showTooltip={showTooltip}
      isFiltersRedesignEnabled={isFiltersRedesignEnabled}
      dropdownContent={
        <div className={styles['dropdown']}>
          <DropdownSelect multiple options={options} value={displayValue} onChange={handleChange} />
        </div>
      }
    />
  );
};
